import React, { useState, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

import usePerformanceInsightsApi, { SUGGESTED_ACTION_TYPE } from './config';
import InsightDetailDrawer from './InsightDetailDrawer';
import ActionFormDrawer from '../performance-actions/ActionFormDrawer';
import api from '../../services/api';
import { parseTableParams } from '../../utils/Utils';
import DataTable from '../../components/ui/DataTable';
import OptionsMenu from '../../components/ui/OptionsMenu';
import GenericFormDrawer from '../../components/GenericFormDrawer';

// Analitik Plan 4 (spec §6, §9) — Bulgular (yalnız İK): kural motorunun ürettiği bulgular, detay drawer'ı, aksiyon açma
// (bulgu "aksiyon açıldı" olur; aynı bulgu + tür için ikinci istek mevcut aksiyonu döndürür) ve geçersiz işaretleme
// (gerekçe zorunlu; gerekçe denetim kaydına yazılmaz). Varsayılan görünüm canlı bulgular (yeni/inceleniyor/aksiyon açıldı).
const Container = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 0;
`;

const DEFAULT_PARAMS = { pageSize: 10, pageNumber: 1, 'status[in]': 'new,reviewing,actioned' };

export default function PerformanceInsightsPage() {
  const { t } = useTranslation();
  // Faz 2 Plan 3 — İK Operasyon Panosu "Tümünü Gör" bağlantısı kural süzgeciyle gelir (?ruleCode=); kullanıcı tabloda başka
  // bir kural seçene kadar süzgeç korunur.
  const location = useLocation();
  const linkedRule = useMemo(() => new URLSearchParams(location.search).get('ruleCode') || null, [location.search]);
  const [queryParams, setQueryParams] = useState(() => (linkedRule ? { ...DEFAULT_PARAMS, 'ruleCode[eq]': linkedRule } : DEFAULT_PARAMS));
  const [filterList, setFilterList] = useState([]);
  const [detail, setDetail] = useState(null);
  const [actionFor, setActionFor] = useState(null);
  const [invalidFor, setInvalidFor] = useState(null);

  const {
    fetching, data, pagination, columns: baseColumns, changeStatus, refresh,
  } = usePerformanceInsightsApi(queryParams);

  const handleDataStateChange = useCallback((action, tableState) => {
    setFilterList(tableState.filterList);
    const params = parseTableParams(tableState, baseColumns);
    // Durum süzgeci seçilmemişse canlı bulgular (spec §6.2 — süresi dolmuş/kapanmış olanlar süzgeçle görünür).
    if (!params['status[in]']) params['status[in]'] = DEFAULT_PARAMS['status[in]'];
    if (!params['ruleCode[eq]'] && linkedRule) params['ruleCode[eq]'] = linkedRule;
    setQueryParams(params);
  }, [baseColumns, linkedRule]);

  const handleChangeStatus = useCallback(async (id, status, reason) => {
    const ok = await changeStatus(id, status, reason);
    if (ok) setDetail(null);
    return ok;
  }, [changeStatus]);

  const handleCreateAction = useCallback(async (payload) => {
    try {
      await api.post('/v1/performance-actions', payload);
      refresh();
      setDetail(null);
      return true;
    } catch {
      return false;
    }
  }, [refresh]);

  const buildRowActions = useCallback((row) => {
    const live = ['new', 'reviewing', 'actioned'].includes(row.status);
    return [
      { type: 'details', label: t('performance.insight.actions.detail'), onClick: () => setDetail(row) },
      ...(row.status === 'new' ? [{ type: 'open', label: t('performance.insight.actions.review'), onClick: () => handleChangeStatus(row.id, 'reviewing') }] : []),
      ...(live ? [{ type: 'submit_for_approval', label: t('performance.insight.actions.openAction'), onClick: () => setActionFor(row) }] : []),
      ...(row.status === 'actioned' ? [{ type: 'close', label: t('performance.insight.actions.close'), onClick: () => handleChangeStatus(row.id, 'closed') }] : []),
      ...(row.status !== 'invalid' ? [{ type: 'reject', label: t('performance.insight.actions.invalidate'), onClick: () => setInvalidFor(row) }] : []),
    ];
  }, [t, handleChangeStatus]);

  const columns = useMemo(() => [
    ...baseColumns,
    {
      name: 'rowActions',
      label: ' ',
      options: {
        filter: false, sort: false, download: false, print: false,
        customBodyRender: (_, tableMeta) => {
          const row = data[tableMeta.rowIndex];
          return row ? <OptionsMenu actions={buildRowActions(row)} row={row} /> : null;
        },
      },
    },
  ], [baseColumns, data, buildRowActions]);

  const actionDefaults = useMemo(() => (actionFor ? {
    sourceInsightId: actionFor.id,
    actionType: SUGGESTED_ACTION_TYPE[actionFor.ruleCode] || 'custom',
    title: t(`performance.insight.${actionFor.ruleCode}.title`, { defaultValue: actionFor.ruleCode }),
  } : undefined), [actionFor, t]);

  const invalidSchema = useMemo(() => [
    { name: 'reason', label: t('performance.insight.invalidReason'), type: 'textarea', required: true, maxLength: 500, rows: 4 },
  ], [t]);

  return (
    <Container>
      <InsightDetailDrawer
        insight={detail}
        open={!!detail}
        onClose={() => setDetail(null)}
        onChangeStatus={handleChangeStatus}
        onOpenAction={(row) => setActionFor(row)}
        onInvalidate={(row) => setInvalidFor(row)}
      />
      <ActionFormDrawer
        open={!!actionFor}
        defaults={actionDefaults}
        onClose={() => setActionFor(null)}
        onSave={handleCreateAction}
      />
      <GenericFormDrawer
        open={!!invalidFor}
        onClose={() => setInvalidFor(null)}
        title={t('performance.insight.invalidTitle')}
        schema={invalidSchema}
        submitLabel={t('performance.insight.actions.invalidate')}
        onSave={async (form) => {
          const ok = await handleChangeStatus(invalidFor.id, 'invalid', form.reason);
          return ok;
        }}
      />
      <DataTable
        title={t('performance.insight.pageTitle')}
        fetching={fetching}
        columns={columns}
        data={data}
        totalRowCount={pagination.totalRowCount}
        pageIndex={pagination.pageIndex}
        pageSize={pagination.pageSize}
        filterList={filterList}
        onFilterConfirm={setFilterList}
        onTableChanged={handleDataStateChange}
        canCreateNewRecord={false}
      />
    </Container>
  );
}
